import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests using AWS Cognito session
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.log('No active session');
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// ============ ORGANIZATIONS ============

export const getAllOrganizations = async () => {
  const response = await api.get('/api/superadmin/organizations');
  return response.data;
};

export const getOrganizationDetails = async (organizationId: string) => {
  const response = await api.get(`/api/organizations/${organizationId}`);
  return response.data;
};

export const createOrganization = async (data: any) => {
  const response = await api.post('/api/organizations', data);
  return response.data;
};

export const updateOrganization = async (organizationId: string, data: any) => {
  const response = await api.put(`/api/organizations/${organizationId}`, data);
  return response.data;
};

export const deleteOrganization = async (organizationId: string) => {
  const response = await api.delete(`/api/organizations/${organizationId}`);
  return response.data;
};

export const verifyOrganization = async (organizationId: string) => {
  const response = await api.post(`/api/organizations/${organizationId}/verify`);
  return response.data;
};

export const suspendOrganization = async (organizationId: string, reason: string) => {
  const response = await api.post(`/api/organizations/${organizationId}/suspend`, { reason });
  return response.data;
};

export const activateOrganization = async (organizationId: string) => {
  const response = await api.post(`/api/organizations/${organizationId}/activate`);
  return response.data;
};

export const getOrganizationMetrics = async (organizationId: string) => {
  const response = await api.get(`/api/organizations/${organizationId}/metrics`);
  return response.data;
};

export const getOrganizationMembers = async (organizationId: string) => {
  const response = await api.get(`/api/organizations/${organizationId}/members`, { params: { pageSize: 100 } });
  return response.data?.members ?? [];
};

// ============ SYSTEM-WIDE USERS ============

export const getAllUsers = async (params?: {
  organizationId?: string;
  role?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await api.get('/api/superadmin/users', { params });
  // API returns { data: [...], totalCount, page, ... }
  return response.data?.data || [];
};

export const getUserDetails = async (userId: string) => {
  const response = await api.get(`/api/superadmin/users/${userId}`);
  return response.data;
};

export const createUser = async (userData: {
  organizationId: string;
  role: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}) => {
  const response = await api.post('/api/superadmin/users', userData);
  return response.data;
};

export const suspendUser = async (userId: string, reason: string) => {
  const response = await api.post(`/api/superadmin/users/${userId}/suspend`, { reason });
  return response.data;
};

export const activateUser = async (userId: string) => {
  const response = await api.post(`/api/superadmin/users/${userId}/activate`);
  return response.data;
};

export const deleteUser = async (userId: string) => {
  const response = await api.delete(`/api/superadmin/users/${userId}`);
  return response.data;
};

export const transferUserOrganization = async (
  userId: string,
  fromOrgId: string,
  toOrgId: string
) => {
  const response = await api.post(`/api/superadmin/users/${userId}/transfer`, {
    fromOrganizationId: fromOrgId,
    toOrganizationId: toOrgId,
  });
  return response.data;
};

// ============ SYSTEM METRICS ============

export const getSystemMetrics = async () => {
  const response = await api.get('/api/superadmin/metrics');
  return response.data;
};

export const getSystemHealth = async () => {
  const response = await api.get('/api/superadmin/health');
  return response.data;
};

export const getSystemAnalytics = async (params?: {
  startDate?: string;
  endDate?: string;
  metric?: string;
}) => {
  const response = await api.get('/api/superadmin/analytics', { params });
  return response.data;
};

export const getActivityLogs = async (params?: {
  organizationId?: string;
  userId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await api.get('/api/superadmin/activity-logs', { params });
  return response.data;
};

export const getAuditLogs = async (params?: {
  organizationId?: string;
  userId?: string;
  resource?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await api.get('/api/superadmin/audit-logs', { params });
  return response.data;
};

// ============ ORGANIZATION TEMPLATES ============

export const getOrganizationTemplates = async () => {
  const response = await api.get('/api/organization-templates');
  return response.data;
};

export const createOrganizationTemplate = async (data: any) => {
  const response = await api.post('/api/organization-templates', data);
  return response.data;
};

export const updateOrganizationTemplate = async (templateId: string, data: any) => {
  const response = await api.put(`/api/organization-templates/${templateId}`, data);
  return response.data;
};

export const deleteOrganizationTemplate = async (templateId: string) => {
  const response = await api.delete(`/api/organization-templates/${templateId}`);
  return response.data;
};

// ============ STATISTICS ============

export const getDashboardStats = async () => {
  const response = await api.get('/api/superadmin/dashboard-stats');
  return response.data;
};

export const getOrganizationStats = async () => {
  const response = await api.get('/api/superadmin/organization-stats');
  return response.data;
};

export const getUserStats = async () => {
  const response = await api.get('/api/superadmin/user-stats');
  return response.data;
};

export default {
  // Organizations
  getAllOrganizations,
  getOrganizationDetails,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  verifyOrganization,
  suspendOrganization,
  activateOrganization,
  getOrganizationMetrics,
  getOrganizationMembers,
  // Users
  getAllUsers,
  getUserDetails,
  suspendUser,
  activateUser,
  deleteUser,
  transferUserOrganization,
  // Metrics
  getSystemMetrics,
  getSystemHealth,
  getSystemAnalytics,
  getActivityLogs,
  getAuditLogs,
  // Templates
  getOrganizationTemplates,
  createOrganizationTemplate,
  updateOrganizationTemplate,
  deleteOrganizationTemplate,
  // Stats
  getDashboardStats,
  getOrganizationStats,
  getUserStats,
};
